import { apiUrl } from "../../../config";
import axios from "axios";

/**
 * Messagerie : la boîte Gmail de l'utilisateur, lue et pilotée via l'API
 * (api/messagerie/*). Rien n'est stocké côté CRM hormis les jetons OAuth.
 *
 * Seuls le compte et les libellés vivent dans le store (partagés entre la
 * page et d'éventuels badges) ; les listes et conversations sont rendues par
 * les actions et gardées par la page, qui les renouvelle sans cesse.
 *
 * Les erreurs Google arrivent avec un `code` (gmail_reconnexion,
 * gmail_non_connecte…) et un statut 409 / 422 / 429 / 503, jamais 401 : un
 * 401 déconnecterait l'utilisateur du CRM (intercepteur de main.js).
 */

// En-têtes posés à chaque requête, comme dans les autres modules (il n'y a
// pas d'intercepteur axios pour l'authentification).
function entetes(json = true) {
  const valeurs = {
    "X-Requested-With": "XMLHttpRequest",
    Authorization: "Bearer " + localStorage.getItem("auth"),
  };
  // Multipart : pas de Content-Type explicite, axios doit poser lui-même la
  // frontière, sinon le serveur ne sait pas découper le corps.
  if (json) {
    valeurs["Content-Type"] = "application/json";
  }
  return valeurs;
}

function appel(configuration) {
  return new Promise((resolve, reject) => {
    axios(configuration)
      .then((response) => resolve(response.data ? response.data.data : null))
      .catch((e) => {
        console.log(e);
        reject(e);
      });
  });
}

/**
 * Envoi et brouillon partagent le même format que api/send-notif : `data` en
 * chaîne JSON, fichiers sous fichiers[].
 */
function formulaire(data, fichiers) {
  const corps = new FormData();
  corps.append("data", JSON.stringify(data));
  (fichiers || []).forEach((fichier) => corps.append("fichiers[]", fichier));
  return corps;
}

export const state = {
  // Réponse de api/messagerie/compte : configure, connecte, a_reconnecter,
  // email, nom, photo, signature, avertissement.
  compte: null,
  libelles: [],
};

export const mutations = {
  SET_COMPTE_MESSAGERIE(state, valeur) {
    state.compte = valeur;
  },
  SET_LIBELLES_MESSAGERIE(state, valeur) {
    state.libelles = valeur || [];
  },
};

export const getters = {
  compteMessagerie(state) {
    return state.compte;
  },
  libellesMessagerie(state) {
    return state.libelles;
  },
  // Conversations non lues de la boîte de réception (badge, titre d'onglet).
  nonLusBoite(state) {
    const boite = state.libelles.find((libelle) => libelle.id === "INBOX");
    return boite ? boite.non_lus : 0;
  },
};

export const actions = {
  getCompteMessagerie({ commit }) {
    return appel({
      method: "get",
      url: apiUrl + "api/messagerie/compte",
      headers: entetes(),
    }).then((compte) => {
      commit("SET_COMPTE_MESSAGERIE", compte);
      return compte;
    });
  },

  // Adresse de la page de consentement Google : la page y redirige le
  // navigateur, Google le renvoie ensuite sur /messagerie?gmail=…
  // eslint-disable-next-line no-unused-vars
  urlConnexionGmail({ commit }) {
    return appel({
      method: "get",
      url: apiUrl + "api/messagerie/google/url",
      headers: entetes(),
    }).then((data) => data.url);
  },

  deconnecterGmail({ commit }) {
    return appel({
      method: "delete",
      url: apiUrl + "api/messagerie/compte",
      headers: entetes(),
    }).then(() => {
      commit("SET_LIBELLES_MESSAGERIE", []);
      commit("SET_COMPTE_MESSAGERIE", null);
    });
  },

  getLibellesMessagerie({ commit }) {
    return appel({
      method: "get",
      url: apiUrl + "api/messagerie/libelles",
      headers: entetes(),
    }).then((libelles) => {
      commit("SET_LIBELLES_MESSAGERIE", libelles);
      return libelles;
    });
  },

  // eslint-disable-next-line no-unused-vars
  creerLibelleMessagerie({ commit }, { nom }) {
    return appel({
      method: "post",
      url: apiUrl + "api/messagerie/libelles",
      data: { nom },
      headers: entetes(),
    });
  },

  /**
   * Liste de conversations. Paramètres : libelle, categorie, q, dossier,
   * page (jeton de page Gmail), par_page.
   * Résout { conversations, page_suivante, estimation, dossier }.
   */
  // eslint-disable-next-line no-unused-vars
  getConversations({ commit }, parametres = {}) {
    return appel({
      method: "get",
      url: apiUrl + "api/messagerie/conversations",
      params: parametres,
      headers: entetes(),
    });
  },

  // eslint-disable-next-line no-unused-vars
  getConversation({ commit }, { id, marquerLu = true }) {
    return appel({
      method: "get",
      url: apiUrl + "api/messagerie/conversations/" + id,
      params: marquerLu ? {} : { marquer_lu: 0 },
      headers: entetes(),
    });
  },

  /** Ajoute / retire des libellés Gmail (UNREAD, STARRED, INBOX, SPAM, Label_…). */
  // eslint-disable-next-line no-unused-vars
  modifierConversations({ commit }, { ids, ajouter = [], retirer = [] }) {
    return appel({
      method: "post",
      url: apiUrl + "api/messagerie/conversations/modifier",
      data: { ids, ajouter, retirer },
      headers: entetes(),
    });
  },

  // eslint-disable-next-line no-unused-vars
  corbeilleConversations({ commit }, { ids }) {
    return appel({
      method: "post",
      url: apiUrl + "api/messagerie/conversations/corbeille",
      data: { ids },
      headers: entetes(),
    });
  },

  // eslint-disable-next-line no-unused-vars
  restaurerConversations({ commit }, { ids }) {
    return appel({
      method: "post",
      url: apiUrl + "api/messagerie/conversations/restaurer",
      data: { ids },
      headers: entetes(),
    });
  },

  /**
   * Pièce jointe en Blob. L'URL de l'API exige le jeton bearer : impossible
   * de la mettre telle quelle dans un <a href> ou un <img src>.
   */
  // eslint-disable-next-line no-unused-vars
  getPieceJointe({ commit }, { messageId, partId, apercu = false }) {
    return new Promise((resolve, reject) => {
      axios
        .get(
          apiUrl + "api/messagerie/messages/" + messageId + "/pieces-jointes/" + partId,
          {
            params: apercu ? { apercu: 1 } : {},
            responseType: "blob",
            headers: entetes(false),
          }
        )
        .then((response) => resolve(response.data))
        .catch((e) => {
          console.log(e);
          reject(e);
        });
    });
  },

  /** Envoi : `data` = champs du message (cf. MessagerieController::envoyer). */
  // eslint-disable-next-line no-unused-vars
  envoyerMessage({ commit }, { data, fichiers }) {
    return appel({
      method: "post",
      url: apiUrl + "api/messagerie/envoyer",
      data: formulaire(data, fichiers),
      headers: entetes(false),
    });
  },

  // eslint-disable-next-line no-unused-vars
  enregistrerBrouillonMessage({ commit }, { data, fichiers }) {
    return appel({
      method: "post",
      url: apiUrl + "api/messagerie/brouillons",
      data: formulaire(data, fichiers),
      headers: entetes(false),
    });
  },

  // eslint-disable-next-line no-unused-vars
  getBrouillonMessage({ commit }, { id }) {
    return appel({
      method: "get",
      url: apiUrl + "api/messagerie/brouillons/" + id,
      headers: entetes(),
    });
  },

  // eslint-disable-next-line no-unused-vars
  supprimerBrouillonMessage({ commit }, { id }) {
    return appel({
      method: "delete",
      url: apiUrl + "api/messagerie/brouillons/" + id,
      headers: entetes(),
    });
  },

  /** Contacts du CRM (équipe, clients, tiers, intervenants) pour « À / Cc / Cci ». */
  // eslint-disable-next-line no-unused-vars
  rechercherContactsMessagerie({ commit }, { q }) {
    return appel({
      method: "get",
      url: apiUrl + "api/messagerie/contacts",
      params: { q },
      headers: entetes(),
    });
  },

  // eslint-disable-next-line no-unused-vars
  rechercherDossiersMessagerie({ commit }, { q }) {
    return appel({
      method: "get",
      url: apiUrl + "api/messagerie/dossiers",
      params: { q },
      headers: entetes(),
    });
  },
};
